/* eslint-disable no-unused-expressions */
import flow from 'lodash/fp/flow';
import React from 'react';
import { useNavigate } from 'react-router-dom';

import { showDeleteConfirm, showCancelConfirm } from '@utils/modal';
import { isFormValidationError } from '@utils/form';
import { getAnyQueryError, withOnSuccessFp, withOnErrorFp } from '@utils/query';
import { notification } from '@atoms';
import { PageForm } from '@components/organisms';

const noopQueryHook = () => [
  () => {
    console.warn('No react-query hook has been passed');
  },
  {},
];

const useVersionedEntity = ({
  useDelete = noopQueryHook,
  useSaveDraft,
  usePublish,
  entityPath,
  redirectTo = entityPath,
  getFormValues: _getFormValues,
  onError,
  doRedirect = 'list',
  saveDraftUseRedirect = doRedirect,
  publishUseRedirect = doRedirect,
  deleteUseRedirect = doRedirect,
  throwOnError = false,
  showNotification = true,
  getId = ({ id } = {}) => id,
  getLocale = ({ language } = {}) => language,
  redirectPath,
}) => {
  const [submitError, setSubmitError] = React.useState();
  const navigate = useNavigate();
  const redirectToPath = () => navigate(redirectPath);
  const redirectToList = () => navigate(redirectTo);
  const redirectToId = (id) => navigate(`/${entityPath}/${id}`);
  const redirectToIdAndLocale = (id, locale) => navigate(`/${entityPath}/${id}/${locale}`);
  const defaultQueryConfig = { onError, throwOnError };

  const withMaybeRedirect = (_doRedirect) =>
    withOnSuccessFp((data) => {
      if (redirectPath) {
        redirectToPath();
      }

      if (_doRedirect === 'id') {
        redirectToId(getId(data));
      }

      if (_doRedirect === 'locale') {
        redirectToIdAndLocale(getId(data), getLocale(data));
      }
      // handles both 'list' and just true
      else if (_doRedirect) {
        redirectToList();
      }
    });

  const withMaybeSuccessNotification = (options) =>
    withOnSuccessFp(() => showNotification && notification.success(options));

  const withMaybeErrorNotification = (options) => withOnErrorFp(() => showNotification && notification.error(options));

  const [saveDraftMutation, saveDraftQuery] = useSaveDraft(
    flow(
      withMaybeRedirect(saveDraftUseRedirect),
      withMaybeSuccessNotification({ message: 'Saved successfully!' }),
      withMaybeErrorNotification({ message: 'An error occurred while saving' }),
    )(defaultQueryConfig),
  );
  const [publishMutation, publishQuery] = usePublish(
    flow(
      withMaybeRedirect(publishUseRedirect),
      withMaybeSuccessNotification({ message: 'Published successfully!' }),
      withMaybeErrorNotification({ message: 'An error occurred while publishing' }),
    )(defaultQueryConfig),
  );
  const [deleteMutation, deleteQuery] = useDelete(
    flow(
      withMaybeRedirect(deleteUseRedirect === 'list'),
      withMaybeSuccessNotification({ message: 'Deleted successfully!' }),
      withMaybeErrorNotification({ message: 'An error occurred while deleting' }),
    )(defaultQueryConfig),
  );

  const error = submitError || getAnyQueryError([saveDraftQuery, publishQuery, deleteQuery]);

  const isSavingDraft = saveDraftQuery.isLoading;
  const isPublishing = publishQuery.isLoading;
  const isDeleting = deleteQuery.isLoading;

  const reset = () => {
    saveDraftQuery?.reset?.();
    publishQuery?.reset?.();
    deleteQuery?.reset?.();
  };

  // swallow errors about form validation but throw any other (not sure if any would happen)
  const getFormValues = async () => {
    try {
      const values = await _getFormValues();
      return values;
    } catch (e) {
      if (!isFormValidationError(e)) {
        setSubmitError(e);
        throw e;
      }
      return null;
    }
  };

  const handleDelete = ({ warningText, ...props }) => {
    showDeleteConfirm({
      onOk: () => {
        setSubmitError(null);
        deleteMutation(props);
      },
      content: warningText,
    });
  };

  const handleCancel = ({ warningText }) => {
    showCancelConfirm({
      onOk: () => {
        setSubmitError(null);
        redirectToList();
      },
      content: warningText,
    });
  };

  const handleSaveDraft = async (props, options) => {
    setSubmitError(null);
    const values = await getFormValues();
    if(values) await saveDraftMutation({ ...props, values }, options);
  };

  const handlePublish = async (props, options) => {
    setSubmitError(null);
    const values = await getFormValues();
    if (values) await publishMutation({ ...props, values }, options);
  };

  return {
    handleSaveDraft,
    handlePublish,
    handleDelete,
    handleCancel,
    isSavingDraft,
    isPublishing,
    isDeleting,
    error,
    doRedirect: redirectToList,
    reset,
  };
};

export default useVersionedEntity;
